import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Plus, Server, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, rupiah, todayStr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { MoneyInput } from "@/components/MoneyInput";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Field } from "@/components/common";
import { InvoiceStatus } from "@/components/InvoiceStatus";

const EMPTY = { name: "", storage: "", ram: "", expires_at: "", rent_fee: "" };

const ServerForm = ({ open, initial, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial, rent_fee: String(initial.rent_fee || ""), expires_at: initial.expires_at || "" } : EMPTY); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const body = { name: f.name, storage: f.storage, ram: f.ram, expires_at: f.expires_at || null, rent_fee: Number(f.rent_fee) || 0 };
    try { if (initial) await api.put(`/servers/${initial.id}`, body); else await api.post("/servers", body); toast.success("Server disimpan"); onClose(); onSaved(); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="server-form">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Edit Server" : "Tambah Server"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nama Server"><Input required className="h-11" value={f.name} onChange={set("name")} data-testid="server-name-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Storage"><Input className="h-11" placeholder="cth. 100 GB" value={f.storage} onChange={set("storage")} data-testid="server-storage-input" /></Field>
            <Field label="RAM"><Input className="h-11" placeholder="cth. 8 GB" value={f.ram} onChange={set("ram")} data-testid="server-ram-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Masa Berlaku"><Input type="date" className="h-11" value={f.expires_at} onChange={set("expires_at")} data-testid="server-expires-input" /></Field>
            <Field label="Biaya Sewa"><MoneyInput value={f.rent_fee} onChange={(v) => setF({ ...f, rent_fee: v })} className="h-11" testId="server-fee-input" /></Field>
          </div>
          <DialogFooter><Button type="submit" className="rounded-full h-11 px-6" data-testid="server-submit">Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LinkDialog = ({ s, onClose, onSaved }) => {
  const [all, setAll] = useState([]);
  const [sel, setSel] = useState([]);
  useEffect(() => { if (s) { setSel(s.wilayah_ids || []); api.get("/wilayah").then((r) => setAll(r.data)).catch(() => setAll([])); } }, [s]);
  const save = async () => { try { await api.put(`/servers/${s.id}/wilayah`, { wilayah_ids: sel }); toast.success("Kantor wilayah ditautkan"); onClose(); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={Boolean(s)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="server-link-dialog">
        <DialogHeader><DialogTitle className="font-heading">Tautkan ke {s?.name}</DialogTitle><DialogDescription>Pilih kantor wilayah yang memakai server ini.</DialogDescription></DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto rounded-2xl border divide-y">
          {all.map((w) => (
            <label key={w.id} className="flex items-center gap-3 px-4 py-3 cursor-pointer" data-testid={`server-link-${w.id}`}>
              <Checkbox checked={sel.includes(w.id)} onCheckedChange={(v) => setSel((x) => (v ? [...x, w.id] : x.filter((i) => i !== w.id)))} />
              <span className="flex-1 text-sm font-medium">{w.name}</span>
              <span className={`text-[11px] ${w.active ? "text-emerald-600" : "text-rose-600"}`}>{w.active ? "Aktif" : "Nonaktif"}</span>
            </label>
          ))}
          {all.length === 0 && <p className="p-4 text-sm text-muted-foreground text-center">Belum ada kantor wilayah.</p>}
        </div>
        <DialogFooter><Button className="rounded-full h-11 px-6" onClick={save} data-testid="server-link-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Lamp = ({ on }) => (
  <span className="relative flex h-3 w-3">
    {on && <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />}
    <span className={`relative inline-flex h-3 w-3 rounded-full ${on ? "bg-green-500" : "bg-neutral-400"}`} />
  </span>
);

const ServerCard = ({ s, onOpen, onEdit, onDelete, onToggle }) => (
  <div className="rounded-3xl border bg-card p-5 shadow-sm hover:border-primary transition-colors cursor-pointer" onClick={() => onOpen(s)} data-testid={`server-card-${s.id}`}>
    <div className="flex items-start gap-3">
      <span className="relative h-12 w-12 rounded-2xl bg-primary/12 text-primary grid place-items-center shrink-0"><Server className="h-6 w-6" /><span className="absolute -top-1 -right-1" data-testid={`server-lamp-${s.id}`}><Lamp on={s.active} /></span></span>
      <div className="min-w-0 flex-1"><p className="font-heading font-semibold truncate">{s.name}</p><p className="text-xs text-muted-foreground">{s.storage || "-"} · RAM {s.ram || "-"}</p></div>
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full" onClick={() => onEdit(s)} aria-label="Edit" data-testid={`server-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full text-rose-600" onClick={() => onDelete(s)} aria-label="Hapus" data-testid={`server-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        <Switch checked={s.active} onCheckedChange={(v) => onToggle(s, v)} data-testid={`server-toggle-${s.id}`} />
      </div>
    </div>
    <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
      <div><p className="text-muted-foreground">Masa berlaku</p><p className={`font-medium ${s.expires_at && s.expires_at < todayStr() ? "text-rose-600" : ""}`}>{s.expires_at ? fmtDate(s.expires_at) : "-"}</p></div>
      <div><p className="text-muted-foreground">Biaya sewa</p><p className="font-mono font-medium">{rupiah(s.rent_fee || 0)}</p></div>
    </div>
    <p className="mt-3 text-xs text-muted-foreground truncate" data-testid={`server-wilayah-${s.id}`}>{s.wilayah.length ? `${s.wilayah.length} wilayah: ${s.wilayah.map((w) => w.name).join(", ")}` : "Belum ada kantor wilayah tertaut"}</p>
  </div>
);

const ServerSection = () => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ open: false, initial: null });
  const [link, setLink] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => { api.get("/servers").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (s, v) => { try { const { data } = await api.patch(`/servers/${s.id}/status`, { active: v }); toast.success(`Server ${v ? "aktif" : "nonaktif"} · ${data.wilayah_updated} kantor wilayah diperbarui`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="mb-8" data-testid="server-section">
      <div className="flex items-center justify-between mb-4"><h2 className="font-heading text-lg font-bold">Server</h2>
        <Button className="rounded-full h-10" onClick={() => setForm({ open: true, initial: null })} data-testid="add-server-button"><Plus className="h-4 w-4 mr-1" />Tambah Server</Button></div>
      {items === null ? <p className="text-muted-foreground">Memuat...</p> : items.length === 0 ? <p className="panel text-sm text-muted-foreground text-center" data-testid="server-empty">Belum ada server.</p> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{items.map((s) => <ServerCard key={s.id} s={s} onOpen={setLink} onEdit={(x) => setForm({ open: true, initial: x })} onDelete={setDel} onToggle={toggle} />)}</div>
      )}
      <ServerForm open={form.open} initial={form.initial} onClose={() => setForm({ open: false, initial: null })} onSaved={load} />
      <LinkDialog s={link} onClose={() => setLink(null)} onSaved={load} />
      <ConfirmDialog open={Boolean(del)} onOpenChange={(o) => !o && setDel(null)} title="Hapus Server" danger confirmLabel="Ya, hapus" onConfirm={async () => { try { await api.delete(`/servers/${del.id}`); toast.success("Server dihapus"); load(); } catch (e) { toast.error(errMsg(e)); } }}
        description={del && <>Hapus server <b>{del.name}</b>? Status kantor wilayah tidak berubah.</>} />
    </section>
  );
};

const BillingSection = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/billing/wilayah").then((r) => setRows(r.data.filter((w) => w.billing?.enabled && w.last_invoice && w.last_invoice.status !== "lunas"))).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <section className="mb-8" data-testid="dashboard-billing-section">
      <div className="flex items-center justify-between mb-4"><h2 className="font-heading text-lg font-bold">Tagihan Layanan Belum Dibayar</h2><Link to="/tagihan-layanan" className="text-sm text-primary font-medium">Lihat semua</Link></div>
      <div className="space-y-3">{rows.map((w) => (
        <Link key={w.id} to={`/tagihan-layanan/${w.id}`} className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-3xl border bg-card px-5 py-4 shadow-sm hover:border-primary transition-colors" data-testid={`dashboard-billing-${w.id}`}>
          <p className="font-heading font-semibold flex-1 min-w-[160px]">{w.name}</p>
          <span className="text-sm text-muted-foreground">{w.resort_count} resort × {rupiah(w.billing.fee_per_resort)}</span>
          <span className="font-mono font-semibold">{rupiah(w.last_invoice?.amount ?? w.billing.fee_per_resort * w.resort_count)}</span>
          <span className="text-xs text-muted-foreground">{w.last_invoice ? `Jatuh tempo ${fmtDate(w.last_invoice.due_date)}` : `Tagihan tgl ${w.billing.bill_day}`}</span>
          {w.last_invoice ? <InvoiceStatus s={w.last_invoice.status} overdue={w.overdue} /> : <span className="text-xs text-muted-foreground">Belum ada tagihan</span>}
        </Link>
      ))}</div>
    </section>
  );
};

export const SuperAdminPanels = () => <><ServerSection /><BillingSection /></>;
